import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

interface ServiceWorkerContextValues {
  registration: ServiceWorkerRegistration | null;
}

const ServiceWorkerContext = createContext<ServiceWorkerContextValues>({
  registration: null,
});

export const useServiceWorkerContext = () => useContext(ServiceWorkerContext);

// replaced dynamically
// const buildDate = '__DATE__' as string;
// replaced dyanmicaly
const reloadSW = '__RELOAD_SW__' as string;

function ServiceWorkerContextProvider({ children }: { children: ReactNode }) {
  const [registration, setRegistration] =
    useState<ServiceWorkerRegistration | null>(null);
  const [showChunkErrorDialog, setShowChunkErrorDialog] = useState(false);
  const [chunkErrorCount, setChunkErrorCount] = useState(0);
  const [showUpdateProgress, setShowUpdateProgress] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [updateReady, setUpdateReady] = useState(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_, r) {
      setRegistration(r || null);
    },
    onRegisterError(error) {
      // eslint-disable-next-line no-console
      console.error('SW registration error', error);
    },
    immediate: true,
  });

  const handleClose = useCallback(() => {
    setNeedRefresh(false);
  }, [setNeedRefresh]);

  const handleUpdate = useCallback(() => {
    updateServiceWorker(true);
  }, [updateServiceWorker]);

  const handleChunkErrorReload = useCallback(() => {
    setShowChunkErrorDialog(false);
    setChunkErrorCount(0);
    window.location.reload();
  }, []);

  const handleChunkErrorClose = useCallback(() => {
    setShowChunkErrorDialog(false);
  }, []);

  const handleUpdateReady = useCallback(() => {
    setUpdateReady(false);
    setShowUpdateProgress(false);
    updateServiceWorker(true);
  }, [updateServiceWorker]);

  const handleCloseUpdate = useCallback(() => {
    setUpdateReady(false);
    setShowUpdateProgress(false);
  }, []);

  useEffect(() => {
    // Handle service worker messages
    const handleSWMessage = (event: MessageEvent) => {
      const { data } = event;

      switch (data?.type) {
        case 'UPDATE_STARTED':
          setShowUpdateProgress(true);
          setUpdateProgress(0);
          break;
        case 'UPDATE_PROGRESS':
          setUpdateProgress(data.progress);
          break;
        case 'UPDATE_READY':
          setShowUpdateProgress(false);
          setUpdateReady(true);
          break;
        case 'CACHE_UPDATED':
          console.log('Cache updated for:', data.url);
          break;
        default:
          break;
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleSWMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, []);

  useEffect(() => {
    // Force update check on registration
    if (registration) {
      // Check for updates immediately
      registration.update();

      // Set up periodic update checks
      const intervalId = setInterval(() => {
        registration.update();
      }, 5 * 60 * 1000); // Check every 5 minutes

      return () => clearInterval(intervalId);
    }

    // Handle chunk loading errors globally with throttling
    const handleError = (event: ErrorEvent) => {
      const { message, filename } = event;
      if (
        message.includes('Loading chunk') ||
        message.includes('Loading CSS chunk') ||
        filename?.includes('chunk')
      ) {
        console.warn('Chunk loading error detected');

        // Prevent infinite reload by limiting error count and using user confirmation
        setChunkErrorCount((prev) => {
          const newCount = prev + 1;

          // Only show dialog if we haven't shown it recently and error count is reasonable
          if (newCount <= 3 && !showChunkErrorDialog) {
            setShowChunkErrorDialog(true);
          } else if (newCount > 3) {
            console.error(
              'Too many chunk loading errors, please refresh manually'
            );
          }

          return newCount;
        });
      }
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const { reason } = event;
      if (
        reason?.message?.includes('Loading chunk') ||
        reason?.message?.includes('Loading CSS chunk')
      ) {
        console.warn('Chunk loading error detected in promise');

        // Same throttling logic for promise rejections
        setChunkErrorCount((prev) => {
          const newCount = prev + 1;

          if (newCount <= 3 && !showChunkErrorDialog) {
            setShowChunkErrorDialog(true);
          } else if (newCount > 3) {
            console.error(
              'Too many chunk loading errors, please refresh manually'
            );
          }

          return newCount;
        });
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    // Reset error count after some time
    const resetErrorCount = setInterval(() => {
      setChunkErrorCount(0);
    }, 60000); // Reset every minute

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
      clearInterval(resetErrorCount);
    };
  }, [registration, showChunkErrorDialog]);

  // Auto-update if it's a reload SW
  useEffect(() => {
    if (reloadSW === 'true' && registration) {
      registration.update();
    }
  }, [registration]);

  const memoizedContextValues = useMemo(
    () => ({
      registration,
    }),
    [registration]
  );

  return (
    <ServiceWorkerContext.Provider value={memoizedContextValues}>
      {children}
      {needRefresh && (
        <Dialog open onClose={handleClose}>
          <DialogTitle>Cập nhật phiên bản mới</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Phiên bản Bookdee mới vừa được phát hành, bấm <b>Cập nhật</b> ngay
              để trải nghiệm tính năng mới nhất
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Đóng</Button>
            <Button onClick={handleUpdate} autoFocus>
              Cập nhật
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {showChunkErrorDialog && (
        <Dialog open onClose={handleChunkErrorClose}>
          <DialogTitle>Lỗi tải tài nguyên</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Ứng dụng gặp lỗi khi tải một số tài nguyên. Điều này có thể do
              phiên bản mới đã được cập nhật. Bạn có muốn tải lại trang để khắc
              phục không?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleChunkErrorClose}>Hủy</Button>
            <Button onClick={handleChunkErrorReload} autoFocus>
              Tải lại trang
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {showUpdateProgress && (
        <Dialog open>
          <DialogTitle>Đang tải phiên bản mới</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Hệ thống đang tải các file mới trong background để đảm bảo trải
              nghiệm mượt mà...
            </DialogContentText>
            <div
              style={{ marginTop: 16, display: 'flex', alignItems: 'center' }}
            >
              <div style={{ width: '100%', marginRight: 16 }}>
                <div
                  style={{
                    background: '#f0f0f0',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      background: '#1976d2',
                      height: 8,
                      width: `${updateProgress}%`,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>
              <span style={{ minWidth: 40, textAlign: 'right' }}>
                {updateProgress}%
              </span>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {updateReady && (
        <Dialog open onClose={handleCloseUpdate}>
          <DialogTitle>Phiên bản mới đã sẵn sàng!</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tất cả file mới đã được tải xuống thành công. Bạn có muốn kích
              hoạt phiên bản mới ngay bây giờ không?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUpdate}>Để sau</Button>
            <Button onClick={handleUpdateReady} autoFocus>
              Kích hoạt ngay
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </ServiceWorkerContext.Provider>
  );
}

export default ServiceWorkerContextProvider;
